import React, { useEffect } from 'react';
import { ClientData } from '../types/ClientData';
import CurrencyInput from '../components/CurrencyInput';

interface Props {
  data: ClientData;
  onUpdate: (updates: Partial<ClientData>) => void;
}

const ToolSession3Setup: React.FC<Props> = ({ data, onUpdate }) => {
  // ── Auto-logic for scenario toggles ─────────────────────────────────────────
  const autoEarlier = (data.targetFreedomAge || 0) >= 58;
  const autoPhased = ((data.targetFreedomAge || 0) - (data.clientAge || 0)) > 7;
  const autoOneOff = (data.childMonthlyCommittedCosts || 0) > 0 || (data.eisSeisBalance || 0) > 0;

  // Auto-populate on mount / data change (only if no manual override)
  useEffect(() => {
    const updates: Partial<ClientData> = {};
    if (!data.s3ScenarioEarlierOverride && data.s3ScenarioEarlierOn !== autoEarlier) {
      updates.s3ScenarioEarlierOn = autoEarlier;
    }
    if (!data.s3ScenarioPhasedOverride && data.s3ScenarioPhasedOn !== autoPhased) {
      updates.s3ScenarioPhasedOn = autoPhased;
    }
    if (!data.s3ScenarioOneOffOverride && data.s3ScenarioOneOffOn !== autoOneOff) {
      updates.s3ScenarioOneOffOn = autoOneOff;
    }
    if (Object.keys(updates).length > 0) onUpdate(updates);
  }, [autoEarlier, autoPhased, autoOneOff, data.s3ScenarioEarlierOverride, data.s3ScenarioPhasedOverride, data.s3ScenarioOneOffOverride, data.s3ScenarioEarlierOn, data.s3ScenarioPhasedOn, data.s3ScenarioOneOffOn, onUpdate]);

  const autoReasons: Record<string, string> = {
    earlier: `Auto: target age ${data.targetFreedomAge || '?'} — ${autoEarlier ? 'room to explore earlier' : 'already targeting early freedom'}`,
    phased: `Auto: ${(data.targetFreedomAge || 0) - (data.clientAge || 0)} years to freedom — ${autoPhased ? 'phased structure relevant' : 'too close to freedom for phased structure'}`,
    oneoff: `Auto: ${autoOneOff ? ((data.childMonthlyCommittedCosts || 0) > 0 ? 'child costs present' : 'EIS/SEIS investments present') : 'no child costs or large assets flagged'}`,
  };

  const toggleScenario = (
    onKey: keyof ClientData,
    overrideKey: keyof ClientData,
    currentOn: boolean,
    currentOverride: boolean
  ) => {
    if (!currentOverride) {
      // First click: enable override and flip
      onUpdate({ [onKey]: !currentOn, [overrideKey]: true });
    } else {
      // Already overridden: just flip
      onUpdate({ [onKey]: !currentOn });
    }
  };

  const resetToAuto = (
    onKey: keyof ClientData,
    overrideKey: keyof ClientData,
    autoValue: boolean
  ) => {
    onUpdate({ [onKey]: autoValue, [overrideKey]: false });
  };

  const scenarios: {
    label: string;
    onKey: keyof ClientData;
    overrideKey: keyof ClientData;
    on: boolean;
    override: boolean;
    autoVal: boolean;
    locked: boolean;
    reason: string;
  }[] = [
    { label: 'Earlier retirement', onKey: 's3ScenarioEarlierOn', overrideKey: 's3ScenarioEarlierOverride', on: data.s3ScenarioEarlierOn, override: data.s3ScenarioEarlierOverride, autoVal: autoEarlier, locked: false, reason: autoReasons.earlier },
    { label: 'Phased retirement', onKey: 's3ScenarioPhasedOn', overrideKey: 's3ScenarioPhasedOverride', on: data.s3ScenarioPhasedOn, override: data.s3ScenarioPhasedOverride, autoVal: autoPhased, locked: false, reason: autoReasons.phased },
    { label: 'One-off spend', onKey: 's3ScenarioOneOffOn', overrideKey: 's3ScenarioOneOffOverride', on: data.s3ScenarioOneOffOn, override: data.s3ScenarioOneOffOverride, autoVal: autoOneOff, locked: false, reason: autoReasons.oneoff },
    { label: 'Stress test', onKey: 's3ScenarioStressOn', overrideKey: 's3ScenarioStressOn', on: true, override: false, autoVal: true, locked: true, reason: 'Always included' },
    { label: 'Longevity', onKey: 's3ScenarioLongevityOn', overrideKey: 's3ScenarioLongevityOn', on: true, override: false, autoVal: true, locked: true, reason: 'Always included' },
    { label: 'Lifestyle business', onKey: 's3ScenarioLifestyleOn', overrideKey: 's3ScenarioLifestyleOn', on: true, override: false, autoVal: true, locked: true, reason: 'Always included' },
  ];

  return (
    <div className="max-w-3xl mx-auto py-8 px-6">
      <h2 className="text-xl font-bold text-ma-navy mb-1">Session 3 Setup</h2>
      <p className="text-sm text-gray-500 mb-6">Configure scenarios, retainer, and homework before Session 3.</p>

      {/* Part A — Scenario Toggles */}
      <div className="mb-8">
        <div className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Scenario Toggles</div>
        <div className="space-y-2">
          {scenarios.map(s => (
            <div key={s.label} className="flex items-center gap-3 py-2 px-3 bg-white rounded-lg border border-gray-100">
              <div className="flex-1 text-sm font-medium text-gray-700">● {s.label}</div>
              <span
                className={`px-3 py-1 rounded-full text-xs font-semibold ${
                  s.on ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-400'
                }`}
              >
                {s.on ? 'ON' : 'OFF'}
              </span>
              <span className="text-xs text-gray-400 w-56 truncate">
                {s.override ? 'Manual override' : s.reason}
              </span>
              {!s.locked ? (
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => toggleScenario(s.onKey, s.overrideKey, s.on, s.override)}
                    className="text-xs text-blue-600 hover:text-blue-800 font-medium"
                  >
                    Manual ↺
                  </button>
                  {s.override && (
                    <button
                      onClick={() => resetToAuto(s.onKey, s.overrideKey, s.autoVal)}
                      className="text-xs text-gray-400 hover:text-gray-600 underline"
                    >
                      Reset to auto
                    </button>
                  )}
                </div>
              ) : (
                <span className="text-xs text-gray-300">locked</span>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Part B — Retainer Setup */}
      <div className="mb-8">
        <div className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Retainer</div>
        <div className="space-y-3">
          <CurrencyInput
            label="Annual retainer fee"
            value={data.retainerFee}
            onChange={v => onUpdate({ retainerFee: v })}
          />
          <div className="mb-4">
            <label className="block text-xs font-medium text-gray-500 mb-1 uppercase tracking-wide">Annual review date</label>
            <input
              type="text"
              value={data.annualReviewDate}
              onChange={e => onUpdate({ annualReviewDate: e.target.value })}
              placeholder="e.g. 01/04/2027"
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-900 focus:border-transparent bg-white"
            />
          </div>
          <div className="mb-4">
            <label className="block text-xs font-medium text-gray-500 mb-1 uppercase tracking-wide">Retainer start date</label>
            <input
              type="text"
              value={data.retainerStartDate}
              onChange={e => onUpdate({ retainerStartDate: e.target.value })}
              placeholder="e.g. 01/05/2026"
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-900 focus:border-transparent bg-white"
            />
          </div>
        </div>
      </div>

      {/* Part C — Session 3 Homework */}
      <div className="mb-8">
        <div className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Homework for Client</div>
        <p className="text-xs text-gray-400 mb-3">Auto-populated in the Annual Review screen.</p>
        <div className="space-y-3">
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1 uppercase tracking-wide">Action 1</label>
            <input
              type="text"
              value={data.s3HomeworkAction1}
              onChange={e => onUpdate({ s3HomeworkAction1: e.target.value })}
              placeholder="e.g. Confirm pension consolidation"
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-900 focus:border-transparent bg-white"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1 uppercase tracking-wide">Action 2</label>
            <input
              type="text"
              value={data.s3HomeworkAction2}
              onChange={e => onUpdate({ s3HomeworkAction2: e.target.value })}
              placeholder="e.g. Review business protection"
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-900 focus:border-transparent bg-white"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default ToolSession3Setup;
